import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const T = {
  primary: "#6366f1",
  primaryLight: "#eef2ff",
  primaryDark: "#4f46e5",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  text: "#0f172a",
  textMuted: "#64748b",
  textLight: "#94a3b8",
  success: "#10b981",
  successBg: "#ecfdf5",
  danger: "#ef4444",
  dangerBg: "#fef2f2",
  warning: "#f59e0b",
  warningBg: "#fffbeb",
  radius: "10px",
  radiusSm: "6px",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd: "0 4px 12px rgba(99,102,241,0.10)",
};

const ContactAvatar = ({ name, primary }) => {
  const initials = (name || "")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "?";
  const colors = ["#6366f1","#8b5cf6","#ec4899","#06b6d4","#10b981","#f59e0b","#ef4444","#3b82f6"];
  const bg = primary ? T.primary : colors[initials.charCodeAt(0) % colors.length];
  return (
    <div style={{ width: 32, height: 32, borderRadius: "50%", background: bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, flexShrink: 0 }}>
      {initials}
    </div>
  );
};

const Th = ({ label, sortKey, sortConfig, onSort }) => (
  <th
    onClick={() => sortKey && onSort(sortKey)}
    style={{
      position: "sticky", top: 0, background: "#f1f5f9", color: T.textMuted,
      fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6,
      padding: "10px 12px", whiteSpace: "nowrap",
      cursor: sortKey ? "pointer" : "default",
      userSelect: "none", borderBottom: `2px solid ${T.border}`, zIndex: 10,
    }}
  >
    {label}
    {sortKey && (
      <span style={{ marginLeft: 4, opacity: sortConfig.key === sortKey ? 1 : 0.3 }}>
        {sortConfig.key === sortKey ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : " ↕"}
      </span>
    )}
  </th>
);

const TdPlain = ({ children, style }) => (
  <td style={{ padding: "10px 12px", fontSize: 13, color: T.text, whiteSpace: "nowrap", ...style }}>
    {children}
  </td>
);

const val = (v) => v || <span style={{ color: T.textLight }}>—</span>;

// ── Confirmation Modal ─────────────────────────────────────────────────────
const ConfirmModal = ({ show, title, message, confirmLabel, onConfirm, onCancel }) => {
  if (!show) return null;
  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050 }} onClick={onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
          zIndex: 1055, width: "100%", maxWidth: 420, background: T.surface,
          borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.18)", padding: "28px 28px 22px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: T.dangerBg }}>
            <i className="bi bi-trash3" style={{ fontSize: 20, color: T.danger }} />
          </div>
          <h6 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: T.text }}>{title}</h6>
        </div>
        <p style={{ color: T.textMuted, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>{message}</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={onConfirm}
            style={{ padding: "7px 20px", borderRadius: T.radiusSm, border: "none", background: T.danger, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
          >
            {confirmLabel}
          </button>
          <button
            onClick={onCancel}
            style={{ padding: "7px 20px", borderRadius: T.radiusSm, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textMuted, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
const AllContacts = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [primaryFilter, setPrimaryFilter] = useState("");

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [downloading, setDownloading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  const [modal, setModal] = useState({ show: false, contact: null });
  const openDeleteModal = (contact) => setModal({ show: true, contact });
  const closeModal = () => setModal({ show: false, contact: null });

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) {
      toast.error("Unauthorized."); navigate("/"); return;
    }
  }, [navigate, token]);

  const fetchContacts = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/contacts/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!res.ok) throw new Error("Failed to fetch contacts"); return res.json(); })
      .then((data) => {
        const list = Array.isArray(data) ? data : data.content || [];
        setContacts(list);
        setFilteredContacts(list);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchContacts(); }, [token]);

  const departments = [...new Set(contacts.map((c) => c.department).filter(Boolean))].sort();
  const customers   = [...new Set(contacts.map((c) => c.customerName).filter(Boolean))].sort();

  useEffect(() => {
    let result = [...contacts];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((c) =>
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.designation?.toLowerCase().includes(q) ||
        c.department?.toLowerCase().includes(q) ||
        c.customerName?.toLowerCase().includes(q)
      );
    }
    if (deptFilter)     result = result.filter((c) => c.department === deptFilter);
    if (customerFilter) result = result.filter((c) => c.customerName === customerFilter);
    if (primaryFilter === "yes") result = result.filter((c) => c.primary);
    if (primaryFilter === "no")  result = result.filter((c) => !c.primary);
    if (sortConfig.key) {
      result.sort((a, b) => {
        const vA = a[sortConfig.key] ?? "";
        const vB = b[sortConfig.key] ?? "";
        if (vA < vB) return sortConfig.direction === "asc" ? -1 : 1;
        if (vA > vB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    setFilteredContacts(result);
  }, [search, deptFilter, customerFilter, primaryFilter, contacts, sortConfig]);

  const handleSort = (key) =>
    setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));

  const handleConfirmDelete = async () => {
    const contact = modal.contact;
    closeModal();
    try {
      const res = await fetch(`${API_BASE_URL}/api/customers/${contact.customerId}/contacts/${contact.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete contact.");
      toast.success("Contact deleted.");
      setContacts((prev) => prev.filter((c) => c.id !== contact.id));
    } catch (err) {
      toast.error(err.message || "Failed to delete contact.");
    }
  };

  const downloadReport = async () => {
    try {
      setDownloading(true);
      const res = await fetch(`${API_BASE_URL}/api/reports/contacts?format=excel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Contact_Report.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
    } catch {
      toast.error("Failed to download report.");
    } finally {
      setDownloading(false);
    }
  };

  const primaryCount = contacts.filter((c) => c.primary).length;

  return (
    <>
      <ConfirmModal
        show={modal.show}
        title="Delete Contact"
        message={`Are you sure you want to permanently delete "${modal.contact?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={closeModal}
      />

      <div className="fixed-top"><AdminNavbar /></div>

      <div style={{ paddingTop: 100, minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', system-ui, sans-serif" }}>

        {/* ── Loading Spinner ── */}
        {loading && (
          <div style={{ position: "fixed", inset: 0, zIndex: 999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(248,250,252,0.85)", backdropFilter: "blur(2px)" }}>
            <style>{`@keyframes cont-spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ width: 44, height: 44, border: `3px solid ${T.primaryLight}`, borderTopColor: T.primary, borderRadius: "50%", animation: "cont-spin 0.7s linear infinite" }} />
            <span style={{ marginTop: 14, fontSize: 13, color: T.textMuted, fontWeight: 500 }}>Loading contacts…</span>
          </div>
        )}

        <div style={{ padding: "20px 24px" }}>

          {/* ── Header ── */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "20px 24px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: T.shadow }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: T.primaryLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-person-lines-fill" style={{ color: T.primary, fontSize: 17 }} />
                </div>
                <h5 style={{ margin: 0, color: T.text, fontWeight: 700, fontSize: 18 }}>Contacts</h5>
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 4, paddingLeft: 2 }}>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.text }}>{contacts.length}</span>Total
                </span>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.primary }}>{primaryCount}</span>Primary
                </span>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.textMuted }}>{contacts.length - primaryCount}</span>Non-primary
                </span>
              </div>
            </div>
            <button
              onClick={() => navigate("/addcontact")}
              style={{ background: T.primary, color: "#fff", border: "none", borderRadius: T.radiusSm, padding: "8px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(99,102,241,0.25)" }}
            >
              <i className="bi bi-person-plus-fill" /> New Contact
            </button>
          </div>

          {/* ── Toolbar ── */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, boxShadow: T.shadow }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

              {/* Search */}
              <div style={{ position: "relative" }}>
                <i className="bi bi-search" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.textLight, fontSize: 13 }} />
                <input
                  type="text"
                  placeholder="Search name, email, role…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 30, paddingRight: 10, paddingTop: 7, paddingBottom: 7, border: `1.5px solid ${search ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, outline: "none", width: 210, color: T.text, transition: "border-color 0.15s", background: T.surface }}
                />
              </div>

              {/* Customer */}
              <select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                style={{ padding: "7px 10px", border: `1.5px solid ${customerFilter ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, color: customerFilter ? T.text : T.textMuted, outline: "none", cursor: "pointer", background: T.surface }}
              >
                <option value="">All Customers</option>
                {customers.map((c) => <option key={c}>{c}</option>)}
              </select>

              {/* Department */}
              <select
                value={deptFilter}
                onChange={(e) => setDeptFilter(e.target.value)}
                style={{ padding: "7px 10px", border: `1.5px solid ${deptFilter ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, color: deptFilter ? T.text : T.textMuted, outline: "none", cursor: "pointer", background: T.surface }}
              >
                <option value="">All Departments</option>
                {departments.map((d) => <option key={d}>{d}</option>)}
              </select>

              {/* Primary */}
              <select
                value={primaryFilter}
                onChange={(e) => setPrimaryFilter(e.target.value)}
                style={{ padding: "7px 10px", border: `1.5px solid ${primaryFilter ? T.primary : T.border}`, borderRadius: T.radiusSm, fontSize: 13, color: primaryFilter ? T.text : T.textMuted, outline: "none", cursor: "pointer", background: T.surface }}
              >
                <option value="">All Types</option>
                <option value="yes">Primary Only</option>
                <option value="no">Non-Primary Only</option>
              </select>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Export */}
              <button
                onClick={downloadReport}
                disabled={downloading}
                title="Download Excel report"
                style={{ padding: "7px 12px", border: `1.5px solid ${T.border}`, borderRadius: T.radiusSm, background: T.surface, color: T.textMuted, fontSize: 13, cursor: downloading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 5, fontWeight: 500, opacity: downloading ? 0.6 : 1 }}
              >
                <i className="bi bi-file-earmark-excel" style={{ color: "#16a34a" }} />
                {downloading ? "Downloading…" : "Export"}
              </button>

              {/* Count pill */}
              <div style={{ padding: "6px 12px", background: T.primaryLight, color: T.primary, borderRadius: T.radiusSm, fontSize: 12, fontWeight: 600 }}>
                {filteredContacts.length} result{filteredContacts.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div style={{ background: T.surface, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, overflow: "hidden" }}>
            <div style={{ maxHeight: "65vh", overflowY: "auto", overflowX: "auto", scrollbarWidth: "thin", scrollbarColor: "#cbd5e1 transparent" }}>
              <style>{`
                .cont-table::-webkit-scrollbar { width: 6px; height: 6px; }
                .cont-table::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
                .cont-table::-webkit-scrollbar-track { background: transparent; }
              `}</style>
              <table className="cont-table" style={{ width: "max-content", minWidth: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th style={{ position: "sticky", top: 0, left: 0, zIndex: 20, background: "#f1f5f9", padding: "10px 14px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, color: T.textMuted, borderBottom: `2px solid ${T.border}`, whiteSpace: "nowrap" }}>
                      View | Edit
                    </th>
                    <Th label="Name"        sortKey="name"         sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Primary"     sortKey={null}         sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Designation" sortKey="designation"  sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Department"  sortKey="department"   sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Email"       sortKey="email"        sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Phone"       sortKey={null}         sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Customer"    sortKey="customerName" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Actions"     sortKey={null}         sortConfig={sortConfig} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.length > 0 ? (
                    filteredContacts.map((c) => (
                      <tr
                        key={c.id}
                        onMouseEnter={() => setHoveredRow(c.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{ background: hoveredRow === c.id ? "#f8faff" : T.surface, transition: "background 0.12s", borderBottom: `1px solid ${T.borderLight}` }}
                      >
                        {/* Sticky actions */}
                        <td style={{ position: "sticky", left: 0, background: hoveredRow === c.id ? "#f8faff" : T.surface, padding: "10px 14px", zIndex: 5, borderRight: `1px solid ${T.borderLight}`, whiteSpace: "nowrap" }}>
                          <Link to={`/viewcontact/${c.id}`} style={{ fontSize: 12, fontWeight: 600, color: T.primary, textDecoration: "none", padding: "3px 8px", borderRadius: 4, background: T.primaryLight, marginRight: 6 }}>
                            View
                          </Link>
                          <Link to={`/editcontact/${c.id}`} style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textDecoration: "none", padding: "3px 8px", borderRadius: 4, background: "#f1f5f9" }}>
                            Edit
                          </Link>
                        </td>

                        {/* Name with avatar */}
                        <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <ContactAvatar name={c.name} primary={c.primary} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{c.name || "—"}</span>
                          </div>
                        </td>

                        {/* Primary badge */}
                        <TdPlain>
                          {c.primary ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: T.primaryLight, color: T.primary, whiteSpace: "nowrap" }}>
                              <i className="bi bi-star-fill" style={{ fontSize: 9 }} /> Primary
                            </span>
                          ) : (
                            <span style={{ color: T.textLight }}>—</span>
                          )}
                        </TdPlain>

                        <TdPlain style={{ color: T.textMuted }}>{val(c.designation)}</TdPlain>

                        {/* Department pill */}
                        <TdPlain>
                          {c.department ? (
                            <span style={{ background: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                              {c.department}
                            </span>
                          ) : <span style={{ color: T.textLight }}>—</span>}
                        </TdPlain>

                        {/* Email */}
                        <TdPlain>
                          {c.email
                            ? <a href={`mailto:${c.email}`} style={{ color: T.primary, textDecoration: "none", fontSize: 13 }}>{c.email}</a>
                            : <span style={{ color: T.textLight }}>—</span>}
                        </TdPlain>

                        <TdPlain style={{ fontFamily: "monospace", fontSize: 12 }}>{val(c.phone)}</TdPlain>

                        {/* Customer link */}
                        <TdPlain>
                          {c.customerId ? (
                            <Link to={`/viewcustomer/${c.customerId}`} style={{ color: T.primary, textDecoration: "none", fontSize: 13, fontWeight: 500 }}>
                              {c.customerName || "—"}
                            </Link>
                          ) : val(c.customerName)}
                        </TdPlain>

                        {/* Delete */}
                        <TdPlain>
                          <button
                            onClick={() => openDeleteModal(c)}
                            title="Delete"
                            style={{ padding: "4px 10px", borderRadius: T.radiusSm, border: `1.5px solid ${T.danger}`, background: T.dangerBg, color: T.danger, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center" }}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </TdPlain>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "48px 20px", color: T.textLight }}>
                        <i className="bi bi-person-lines-fill" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                        {contacts.length === 0 ? "No contacts found." : "No contacts match the current filters."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {error && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: T.dangerBg, color: T.danger, borderRadius: T.radiusSm, fontSize: 13 }}>
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AllContacts;
